/* eslint-disable react-hooks/exhaustive-deps */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import * as Yup from "yup";

import { CGStocksDetailsUploadForm, CGStocksExtractDataForm } from "..";
import { IStocksExtractForm, IStocksFormTypes } from "../../../../config/interfaces";
import {
	StockExtractedFormValues,
	StocksFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";

type Props = {
	showCanvas: boolean;
	formType: IStocksFormTypes;
	handleCloseBtnPress: (StocksData: IStocksExtractForm | null) => void;
};

const CGStocksUploadDetailsForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress, formType } = props;

	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [initFormType, setInitFormType] = useState<IStocksFormTypes>(null);
	const [initFormValues, setInitFormValues] =
		useState<IStocksExtractForm>(StockExtractedFormValues);
	const [showForm, setShowForm] = useState<boolean>(false);

	useEffect(() => {
		if (showCanvas) {
			setInitFormType(() => formType);
		}
	}, [showCanvas]);

	useEffect(() => {
		if (initFormType === "MANUAL") {
			setShowForm(() => true);
		} else {
			setShowForm(() => false);
		}
	}, [initFormType]);

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<Offcanvas.Header>
					<Offcanvas.Title>
						{initFormType === "MANUAL"
							? "Add/Edit Stocks Details"
							: "Upload Stocks Details to Auto-Fill Your Data"}
					</Offcanvas.Title>
					<Button
						variant="primary"
						className="icon-only hide-offcanvas"
						onClick={() => {
							handleCloseBtnPress(null);
							setTimeout(() => {
								setInitFormType(() => null);
							}, 300);
						}}
					>
						<FASvgIcon icon={faXmark} />
					</Button>
				</Offcanvas.Header>

				{initFormType === "MANUAL" && showForm ? (
					<Formik
						enableReinitialize={true}
						initialValues={initFormValues}
						validationSchema={Yup.object().shape({
							long_term: Yup.object().shape({ ...StocksFormSchema }),
							short_term: Yup.object().shape({ ...StocksFormSchema })
						})}
						onSubmit={async (values) => {
							handleCloseBtnPress(values);
						}}
					>
						{({ handleSubmit, submitCount, isValid, errors, validateForm }) => (
							<>
								<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

								<Offcanvas.Body>
									<CGStocksExtractDataForm />
								</Offcanvas.Body>

								<div className="offcanvas-btns">
									{!isFilingReadOnly ? (
										<Button
											variant="outline-white"
											size="sm"
											onClick={async (event) => {
												event.preventDefault();
												await validateForm();
												handleSubmit();
											}}
										>
											Save and Close
										</Button>
									) : (
										<Button
											variant="outline-white"
											size="sm"
											onClick={() => handleCloseBtnPress(null)}
										>
											Close
										</Button>
									)}
								</div>
							</>
						)}
					</Formik>
				) : (
					<Offcanvas.Body>
						<CGStocksDetailsUploadForm
							handleExtractionSuccess={(responseData) => {
								const newFormValues = {
									long_term: {
										...cloneDeep(StockExtractedFormValues.long_term),
										...responseData.long_term
									},
									short_term: {
										...cloneDeep(StockExtractedFormValues.short_term),
										...responseData.short_term
									}
								};
								setInitFormValues(() => cloneDeep(newFormValues));
								setInitFormType(() => "MANUAL");
								setShowForm(() => true);
							}}
							handleAddManually={() => {
								setInitFormType(() => "MANUAL");
								setShowForm(() => true);
							}}
						/>
					</Offcanvas.Body>
				)}
			</Offcanvas>
		</>
	);
};

export default CGStocksUploadDetailsForm;
